import React, { useEffect, useState } from "react";
import { FaPlus, FaSave, FaTimes, FaTrash } from "react-icons/fa";

import ModalShell from "./ModalShell";
import {
  EVENT_CATEGORIES,
  EVENT_LEVELS,
  EVENT_TYPES,
} from "../utils/eventsJudokaUtils";

const inputClassName =
  "min-h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:focus:bg-white/[0.08]";

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-2 block text-sm font-bold text-slate-700 dark:text-white/80">
        {label}
      </label>

      {children}
    </div>
  );
}

function EventFormModal({
  isOpen,
  onClose,
  form,
  onInputChange,
  onSubmit,
  isEditMode,
  saving,
}) {
  const [selectedCate, setSelectedCate] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);

  useEffect(() => {
    setSelectedCategories(
      form.cate_event
        ? String(form.cate_event)
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
        : [],
    );
  }, [form.cate_event]);

  if (!isOpen) return null;

  const handleAddCategory = () => {
    if (!selectedCate || selectedCategories.includes(selectedCate)) return;

    const updatedCategories = [...selectedCategories, selectedCate];

    setSelectedCategories(updatedCategories);
    setSelectedCate("");

    onInputChange({
      target: {
        name: "cate_event",
        value: updatedCategories.join(","),
      },
    });
  };

  const handleRemoveCategory = (category) => {
    const updatedCategories = selectedCategories.filter(
      (item) => item !== category,
    );

    setSelectedCategories(updatedCategories);

    onInputChange({
      target: {
        name: "cate_event",
        value: updatedCategories.join(","),
      },
    });
  };

  return (
    <ModalShell
      title={isEditMode ? "Modifier un événement" : "Créer un événement"}
      eyebrow={isEditMode ? "Mise à jour" : "Nouvel événement"}
      onClose={onClose}
      maxWidth="max-w-4xl"
    >
      <form onSubmit={onSubmit} className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Titre de l'événement" className="sm:col-span-2">
            <input
              type="text"
              id="titre"
              name="titre"
              value={form.titre || ""}
              onChange={onInputChange}
              className={inputClassName}
              placeholder="Ex : Championnat départemental"
              required
            />
          </Field>

          <Field label="Description" className="sm:col-span-2">
            <textarea
              id="description"
              name="description"
              value={form.description || ""}
              onChange={onInputChange}
              rows="4"
              className={`${inputClassName} min-h-28 resize-none leading-6`}
              placeholder="Informations utiles, modalités, convocations..."
            />
          </Field>

          <Field label="Date">
            <input
              type="date"
              id="start"
              name="start"
              value={form.start || ""}
              onChange={onInputChange}
              className={inputClassName}
              required
            />
          </Field>

          <Field label="Type">
            <select
              id="type_event"
              name="type_event"
              value={form.type_event || ""}
              onChange={onInputChange}
              className={inputClassName}
            >
              <option value="">Sélectionnez un type</option>
              {EVENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Niveau">
            <select
              id="level_event"
              name="level_event"
              value={form.level_event || ""}
              onChange={onInputChange}
              className={inputClassName}
            >
              <option value="">Sélectionnez un niveau</option>
              {EVENT_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Catégories d'âge">
            <div className="flex gap-2">
              <select
                value={selectedCate}
                onChange={(event) => setSelectedCate(event.target.value)}
                className={inputClassName}
              >
                <option value="">Ajouter une catégorie</option>
                {EVENT_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={handleAddCategory}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white transition hover:-translate-y-0.5 active:translate-y-0 dark:bg-white dark:text-slate-950"
                aria-label="Ajouter la catégorie"
              >
                <FaPlus />
              </button>
            </div>
          </Field>

          {selectedCategories.length > 0 && (
            <div className="sm:col-span-2">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                Catégories sélectionnées
              </p>

              <div className="flex flex-wrap gap-2">
                {selectedCategories.map((category) => (
                  <span
                    key={category}
                    className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-2 text-xs font-bold text-orange-700 dark:bg-orange-500/10 dark:text-orange-200"
                  >
                    {category}

                    <button
                      type="button"
                      onClick={() => handleRemoveCategory(category)}
                      className="text-red-600 transition hover:scale-110 dark:text-red-300"
                      aria-label={`Retirer ${category}`}
                    >
                      <FaTrash className="text-[10px]" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <Field label="Lieu">
            <input
              type="text"
              id="lieu_event"
              name="lieu_event"
              value={form.lieu_event || ""}
              onChange={onInputChange}
              className={inputClassName}
              placeholder="Ex : Dojo municipal, Bordeaux"
            />
          </Field>

          <Field label="Horaires">
            <input
              type="text"
              id="horaire_event"
              name="horaire_event"
              value={form.horaire_event || ""}
              onChange={onInputChange}
              className={inputClassName}
              placeholder="Ex : Pesée 8h30 / Début 10h"
            />
          </Field>

          <Field label="URL de la photo" className="sm:col-span-2">
            <input
              type="url"
              id="photo_url"
              name="photo_url"
              value={form.photo_url || ""}
              onChange={onInputChange}
              className={inputClassName}
              placeholder="https://exemple.com/photo-evenement.jpg"
            />
          </Field>
        </div>

        <div className="rounded-[1.5rem] border border-orange-200 bg-orange-50 p-4 dark:border-orange-400/20 dark:bg-orange-500/10">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              id="is_shared"
              name="is_shared"
              checked={Boolean(form.is_shared)}
              onChange={onInputChange}
              className="mt-1 h-5 w-5 rounded border-orange-300 text-orange-500 focus:ring-orange-500"
            />

            <span>
              <span className="block text-sm font-bold text-orange-800 dark:text-orange-100">
                Partager cet événement avec la communauté
              </span>

              <span className="mt-1 block text-sm font-medium leading-6 text-orange-700/80 dark:text-orange-100/70">
                Assurez-vous que les informations sont correctes avant de le
                rendre disponible aux autres membres.
              </span>
            </span>
          </label>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 dark:border-white/10 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:hover:bg-white/10"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-orange-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Enregistrement..." : isEditMode ? "Modifier" : "Créer"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

export default EventFormModal;